import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { api, type AgentSchedule } from '@/lib/api';

export type LayoutAgent = {
  id: string; slug: string; name: string;
  kind?: 'cloud' | 'assistant';
  status: 'active' | 'paused' | 'archived';
  systemPrompt?: string | null; soul?: string | null;
  settings?: { timezone?: string; autonomy?: { enabled?: boolean } } | null;
  /** Stored avatar recipe; null = default derived from the id. */
  avatar?: unknown;
};

/** One controller for both presentations; never expose a prior tenant's response. */
export function useAgentEditor(id?: string) {
  const { tenant } = useAuth();
  const key = `${tenant?.id ?? ''}:${id ?? ''}`;
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ key: string; agent: LayoutAgent | null; schedules: AgentSchedule[] | null }>({ key: '', agent: null, schedules: null });
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    let cancelled = false;
    if (!id || !tenant?.id) return;
    Promise.allSettled([api.aiAgents.get(id), api.aiAgents.schedules.list(id)]).then(([agent, schedules]) => {
      if (cancelled) return;
      setState({ key, agent: agent.status === 'fulfilled' ? agent.value as LayoutAgent : null,
        schedules: schedules.status === 'fulfilled' ? schedules.value : null });
    });
    return () => { cancelled = true; };
  }, [id, tenant?.id, key, revision]);
  return { agent: state.key === key ? state.agent : null, schedules: state.key === key ? state.schedules : null, refresh, revision, identity: key };
}
